import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams, Link, Navigate } from 'react-router-dom';
import { facilitiesAPI } from '@/lib/api';
import { canSellPremium } from '@/lib/appUrl';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import {
  ArrowLeft, Check, Crown, Sparkles, BarChart3, TrendingUp,
  ShieldCheck, Zap, Star,
} from 'lucide-react';

const STANDARD_FEATURES = [
  'Pagina pubblica brandizzata',
  'Marketplace strutture',
  'Calendar e prenotazioni',
  'Pagamenti Stripe + payouts',
  'Analytics base',
  'Supporto via email',
];

const PREMIUM_FEATURES = [
  { text: 'Tutto di Standard', icon: Sparkles },
  { text: 'Top results in marketplace', icon: TrendingUp },
  { text: 'Badge "Verificato"', icon: ShieldCheck },
  { text: 'Heatmap occupancy + suggested actions', icon: BarChart3 },
  { text: 'Promozioni illimitate', icon: Zap },
  { text: 'Commissione ridotta al 7%', icon: Star },
  { text: 'Supporto prioritario WhatsApp', icon: Crown },
];

const UpgradeFacilityPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [facility, setFacility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (searchParams.get('cancelled')) {
      toast.info('Upgrade annullato. Puoi riprovare quando vuoi.');
    }
  }, [searchParams]);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await facilitiesAPI.getById(id);
        setFacility(res.data.facility);
      } catch (err) {
        toast.error('Errore nel caricamento');
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [id, navigate]);

  const handleUpgrade = async () => {
    setSubmitting(true);
    try {
      const res = await facilitiesAPI.subscribePremium(id);
      // Redirect to Stripe checkout
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore upgrade');
      setSubmitting(false);
    }
  };

  if (!canSellPremium()) return <Navigate to={`/facilities/${id}/dashboard`} replace />;
  if (loading) {
    return <div className="p-8 text-center text-muted-foreground">Caricamento…</div>;
  }
  if (!facility) return null;

  const isAlreadyPremium = facility.tier === 'premium';

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-5xl mx-auto">
      <Button variant="ghost" className="mb-4" onClick={() => navigate(-1)}>
        <ArrowLeft className="h-4 w-4 mr-2" />
        Indietro
      </Button>

      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-100 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300 text-xs font-semibold mb-3">
          <Crown className="h-3 w-3" />
          Upgrade Premium
        </div>
        <h1 className="text-3xl md:text-4xl font-bold">Cresci più veloce con Premium</h1>
        <p className="text-muted-foreground mt-2">
          Più visibilità, più analytics, commissione ridotta. <strong>30 giorni gratis</strong>, poi €29/mese.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Standard */}
        <Card className="border-2">
          <CardContent className="p-6">
            <div className="text-xs font-semibold text-muted-foreground uppercase mb-1">
              Standard {facility.tier !== 'premium' && '· attuale'}
            </div>
            <div className="text-4xl font-bold">10%<span className="text-base font-normal text-muted-foreground"> commissione</span></div>
            <div className="text-sm text-muted-foreground mt-1">Solo sulle prenotazioni completate</div>
            <ul className="mt-5 space-y-2 text-sm">
              {STANDARD_FEATURES.map((f) => (
                <li key={f} className="flex gap-2">
                  <Check className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> {f}
                </li>
              ))}
            </ul>
            <Button variant="secondary" className="mt-6 w-full" disabled>
              Piano attuale
            </Button>
          </CardContent>
        </Card>

        {/* Premium */}
        <Card className="border-2 border-purple-500 bg-gradient-to-br from-purple-600 to-indigo-600 text-white relative overflow-hidden">
          <div className="absolute top-3 right-3 text-xs bg-white/20 px-2 py-0.5 rounded-full font-semibold flex items-center gap-1">
            <Crown className="h-3 w-3" /> Featured
          </div>
          <CardContent className="p-6">
            <div className="text-xs font-semibold uppercase opacity-80 mb-1">Premium</div>
            <div className="text-4xl font-bold">€29<span className="text-base font-normal opacity-80">/mese + 7%</span></div>
            <div className="text-sm opacity-80 mt-1">Prima dei 30 giorni: €0 · Annulla quando vuoi</div>
            <ul className="mt-5 space-y-2 text-sm">
              {PREMIUM_FEATURES.map((f) => {
                const Icon = f.icon;
                return (
                  <li key={f.text} className="flex gap-2">
                    <Icon className="h-4 w-4 shrink-0 mt-0.5 text-amber-300" /> {f.text}
                  </li>
                );
              })}
            </ul>
            {isAlreadyPremium ? (
              <Button disabled className="mt-6 w-full bg-white/20 text-white border border-white/30">
                Già Premium ✓
              </Button>
            ) : (
              <Button
                onClick={handleUpgrade}
                disabled={submitting}
                className="mt-6 w-full bg-white text-purple-700 hover:bg-white/95 font-bold"
              >
                {submitting ? 'Reindirizzamento…' : 'Inizia 30gg gratis →'}
              </Button>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="mt-10 max-w-2xl mx-auto text-center text-sm text-muted-foreground">
        <p>
          Pagamento sicuro via Stripe. Puoi annullare in qualsiasi momento dalla dashboard.
          Niente carta richiesta per provare? <strong>No</strong>: serve per il dopo i 30 giorni,
          ma <strong>0€ vengono addebitati nei primi 30 giorni</strong>.
        </p>
        <p className="mt-3">
          <Link to={`/facilities/${id}/dashboard`} className="hover:underline">Torna alla dashboard</Link>
        </p>
      </div>
    </div>
  );
};

export default UpgradeFacilityPage;
